import { ref, computed } from 'vue'
import { useQuery } from '@tanstack/vue-query'
import { queryClient } from '@/queryClient.js'
import { useAccontStore } from '@/stores/AccountStore.js'
import { Station } from '@/models/station.js'
import {
	RESOURCE_NAMES,
	FIELD_NAMES,
	readResourceItem,
	updateResourceItem,
	convertToApiTimeDate,
	parseResource,
	formatResource,
} from '@/api/api.js'




// "Date" type pasport fields (flatten object keys)
export const PASSPORT_DATE_PATHS = [
	FIELD_NAMES.updateTime,
	FIELD_NAMES.createTime,
	Station.passportKeys.produceDate,
	Station.passportKeys.installationDate,
	Station.passportKeys.overhaulDate,
	Station.passportKeys.surveyStartTime,
	`${Station.passportKeys.powerUnit.self}.${Station.passportKeys.powerUnit.operaionAndProtectionStartDate}`,
	`${Station.passportKeys.electricMeter.self}.${Station.passportKeys.electricMeter.verificationDate}`,
	`${Station.passportKeys.electricMeter.self}.${Station.passportKeys.electricMeter.surveyStartTime}`,
	`${Station.passportKeys.stabilization.self}.${Station.passportKeys.stabilization.updateTime}`,
	`${Station.passportKeys.measurePotentialOnOffStation.self}.${Station.passportKeys.measurePotentialOnOffStation.startTime}`,
]




export function queryKeyBuilder(stationId) {
	return [
		RESOURCE_NAMES.stationPassports,
		stationId,
	]
}

export function queryKeyHistoryBuilder(stationId, fromTime=null, toTime=null) {
	return [
		RESOURCE_NAMES.stationPassports,
		stationId,
		'history',
		...(fromTime == null ? [] : [convertToApiTimeDate(fromTime)]),
		...(toTime == null ? [] : [convertToApiTimeDate(toTime)]),
	]
}

/**
 * Arguments builder for REST API requests
 * @param {Date|null} fromTime 
 * @param {date|null} toTime 
 * @param {String|Number|null} version - 'all' for all versions or version number
 * @returns 
 */
export function requestArgsBuilder(fromTime, toTime, version='all') {
	const isTimeRange = fromTime !== null || toTime !== null
	let isWhereInArgs = false
	const whereObject = {}

	if (isTimeRange) {
		isWhereInArgs = true
		whereObject[FIELD_NAMES.updateTime] = {
			...(fromTime !== null && { "$gt": convertToApiTimeDate(fromTime) }),
			...(toTime !== null && { "$lt": convertToApiTimeDate(toTime) }),
		}
	}

	// const whereObject = {
	// 	[FIELD_NAMES.updateTime]: {
	// 		...(fromTime !== null && { "$gt": convertToApiTimeDate(fromTime) }),
	// 		...(toTime !== null && { "$lt": convertToApiTimeDate(toTime) })
	// 	}
	// }
	return [
		`embedded={
			"${Station.passportKeys.orgId}":1,
			"${Station.passportKeys.stabilization.self}.${Station.passportKeys.stabilization.actor}":1,
			"${Station.passportKeys.creatorId}":1,
			"${Station.passportKeys.updateBy}":1,
			"${Station.passportKeys.pipelineId}":1
		}`,
		...(isWhereInArgs ? [`where=${JSON.stringify(whereObject)}`] : []),
		// ...(fromTime !== null || toTime !== null ? [`where=${JSON.stringify(whereObject)}`] : []),
		...(version !== null ? [`version=${version}`] : []),
	]
}




/**
 * One station passport.
 * Station passport history.
 * @param {String} stationId - one station id for read passport and history
 * @param {Date} from - time from for history request
 * @param {Date} to - time to history reauest
 * @returns 
 */
export function useStationPassport(stationId=null, from=null, to=null) {
	// console.log('useStationPassport call', stationId, from, to)
	const accountStore = useAccontStore()

	// Actual period
	// Reactive lower bound for fetch time window
	const fromTime = ref(from)
	// Reactive upper bound for fetch time window
	const toTime = ref(to)
	const curStationId = ref(stationId)


	// ========================= Now passport reading =========================
	const passportQuery = useQuery({
		// Include auth identity and time filter in key to auto-refetch on change
		queryKey: computed(
			() => queryKeyBuilder(curStationId.value)
		),
		queryFn: async () => {
			// console.log('Read station passport', curStationId.value)
			return curStationId.value === null
				? undefined
				: await readResourceItem(
					RESOURCE_NAMES.stationPassports,
					curStationId.value,
					accountStore.userLogin,
					accountStore.userPassword,
					requestArgsBuilder(null, null, null),
			)
		},
	})

	const passportNow = computed(() => {
		// Parse time API fields to Date() and return parsed
		return passportQuery?.data?.value
			? parseResource(passportQuery.data.value, PASSPORT_DATE_PATHS)
			: null
	})
	// ========================================================================

	// ====================== Passport history reading ========================
	const querieOptions = computed(() => {
		return {
			// Include auth identity and time filter in key to auto-refetch on change
			queryKey: computed(
				() => queryKeyHistoryBuilder(curStationId.value, fromTime.value, toTime.value)
			),
			queryFn: async () => {
				if (curStationId.value === null)
					return undefined

				const history = []

				// Read with time range
				const response = await readResourceItem(
						RESOURCE_NAMES.stationPassports,
						curStationId.value,
						accountStore.userLogin,
						accountStore.userPassword,
						requestArgsBuilder(fromTime.value, toTime.value)
				)

				// Parse history items and push to history array
				history.push(...response._items.map((item) => parseResource(item, PASSPORT_DATE_PATHS)))

				// Remove last item if is not in time interval (need for eve API endpoint)
				const lastItemUpdateTime = history.at(-1)[FIELD_NAMES.updateTime].getTime()
				if (history.length > 0
					&& (lastItemUpdateTime > toTime.value?.getTime()
					|| lastItemUpdateTime < fromTime.value?.getTime())
				) {
					history.splice(-1, 1)
				}

				// Read lowest document version in records
				const lowestVersion = history[0]?.[FIELD_NAMES.version]

				// If lowest version is not first
				if (Number.isInteger(lowestVersion) && lowestVersion > 1) {
					// Read previous version for correct difference defining
					const prevResp = await readResourceItem(
						RESOURCE_NAMES.stationPassports,
						curStationId.value,
						accountStore.userLogin,
						accountStore.userPassword,
						requestArgsBuilder(null, null, lowestVersion - 1),
					)
					// If previous version is readed
					if (prevResp) {
						delete prevResp._items // delete artifacts
						// Add to history as first record
						history.unshift(parseResource(prevResp, PASSPORT_DATE_PATHS))
					}
				}

				return history
			},
		}
	})
	const passporHistoryQuery = useQuery(querieOptions)

	const passportHistory = computed(() => {
		return passporHistoryQuery?.data.value ?? []
	})
	// ========================================================================
	

	function changeStationId(newStationId) {
		if (newStationId === curStationId.value) {
			return
		}

		curStationId.value = newStationId
	}

	function changePeriod(newFromTime, newToTime) {
		// Updating fromTime, toTime changes queryKey and triggers TanStack Query refetch.
		if (newFromTime == null) {
			fromTime.value == null
		} else if (newFromTime instanceof Date) {
			fromTime.value = new Date(newFromTime)
		} else {
			console.error('setPeriod error: not Date type input')
		}

		if (newToTime == null) {
			toTime.value == null
		} else if (newToTime instanceof Date) {
			toTime.value = new Date(newToTime)
		} else {
			console.error('setPeriod error: not Date type input')
		}
	}

	/**
	 * Write station passport to DB
	 * @param {Object} newPassport - new passport Object with _id an _etag keys
	 * @param {Function} onSuccess  - success write handler
	 * @param {Function} onError - error write handler
	 */
	async function savePassport(newPassport, onSuccess, onError) {
		if (newPassport[Station.passportKeys.id] !== curStationId.value) {
			throw new Error(`Station id mismatch. ${newPassport[Station.passportKeys.id]} !== ${curStationId.value}`)
		}

		try {
			const itemId = newPassport[FIELD_NAMES.id]
			delete newPassport[FIELD_NAMES.id]
			const itemEtag = newPassport[FIELD_NAMES.etag]
			delete newPassport[FIELD_NAMES.etag]
			const formatedPassport = formatResource(newPassport, PASSPORT_DATE_PATHS)

			// Set updator userId
			formatedPassport[Station.passportKeys.updateBy] = accountStore.userId

			// Fetch update request
			const result = await updateResourceItem(
				RESOURCE_NAMES.stationPassports,
				accountStore.userLogin,
				accountStore.userPassword,
				itemId,
				itemEtag,
				formatedPassport,
			)

			if (result._status != 'OK') {
				throw new Error(`Update passport request status: ${result._status}`)
			}

			await queryClient.invalidateQueries({
				queryKey: queryKeyBuilder(curStationId.value),
				exact: false, // inavalidate queries that begins from this key
			})

			// Update passportNow
			// await queryClient.refetchQueries({
			// 	queryKey: queryKeyBuilder(curStationId.value),
			// 	exact: true,
			// })

			if (onSuccess) {
				onSuccess()
			}
		
		} catch (err) {
			console.error("on update passport request", err)
			if (onError) {
				onError(err)
			}
		}	
	}

	return {
		passportNow,
		passportHistory,
		isLoading: passporHistoryQuery.isLoading,
		isError: passporHistoryQuery.isError,
		error: passporHistoryQuery.error,

		// refetch: query.refetch,
		
		changeStationId,
		changePeriod,
		savePassport,
	}
}